import React, { useState } from 'react';

/**
 * AgentPlanCard — displays a proposed plan with per-step checkboxes.
 *
 * Props:
 *   plan: { steps: [{index, action_label, description, tool_name, is_reversible}], session_title }
 *   onConfirm(selectedStepIndices)
 *   onCancel()
 *   locked    — actively executing right now (spinner footer)
 *   cancelled — plan was rejected (greyed, "Plan cancelled" footer)
 *   executed  — plan ran to completion (greyed, "Executed" footer, no buttons)
 */
const AgentPlanCard = ({ plan, onConfirm, onCancel, locked = false, cancelled = false, executed = false }) => {
  const [checked, setChecked] = useState(() =>
    new Set((plan?.steps || []).map((s) => s.index))
  );

  if (!plan) return null;

  const steps = plan.steps || [];
  const selectedCount = checked.size;
  const frozen = locked || cancelled || executed;
  const isInteractive = !frozen;

  const toggleStep = (idx) => {
    if (!isInteractive) return;
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleConfirm = () => {
    if (selectedCount === 0) return;
    onConfirm(Array.from(checked));
  };

  return (
    <div
      className={`border rounded-lg overflow-hidden my-2 ${
        cancelled || executed
          ? 'border-gray-100 bg-gray-50 opacity-60'
          : locked
          ? 'border-gray-200 bg-gray-50'
          : 'border-gray-300 bg-white'
      }`}
    >
      {/* Header */}
      <div className={`px-4 py-3 ${frozen ? 'bg-gray-50' : 'bg-white'}`}>
        <p className="text-sm font-medium text-gray-900">Here's what I'll do:</p>
      </div>

      {/* Steps */}
      <div className="px-4 pb-3 space-y-3">
        {steps.map((step) => {
          const isChecked = frozen ? true : checked.has(step.index);
          return (
            <label
              key={step.index}
              className={`flex items-start gap-3 ${isInteractive ? 'cursor-pointer' : 'cursor-default'}`}
              onClick={() => toggleStep(step.index)}
            >
              <div className="mt-0.5 flex-shrink-0">
                <input
                  type="checkbox"
                  checked={isChecked}
                  readOnly
                  disabled={!isInteractive}
                  className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-0 opacity-50"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {step.index + 1}.
                  </span>
                  <span className="text-xs font-semibold text-gray-700">{step.action_label}</span>
                  {step.is_reversible === false && !frozen && (
                    <span className="text-xs text-orange-500">(irreversible)</span>
                  )}
                </div>
                <p className="text-sm text-gray-700 mt-0.5 leading-snug">{step.description}</p>
              </div>
            </label>
          );
        })}
      </div>

      {/* Footer */}
      {cancelled && (
        <div className="flex items-center gap-2 px-4 py-2 border-t border-gray-100">
          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
          <span className="text-xs text-gray-400">Plan cancelled</span>
        </div>
      )}

      {executed && (
        <div className="flex items-center gap-2 px-4 py-2 border-t border-gray-100">
          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
          <span className="text-xs text-gray-400">Executed</span>
        </div>
      )}

      {!frozen && (
        <>
          <div className="border-t border-gray-200 mx-4" />
          <div className="flex gap-2 px-4 py-3">
            <button
              onClick={handleConfirm}
              disabled={selectedCount === 0}
              className={`flex-1 py-2 px-3 rounded text-sm font-medium transition-colors ${
                selectedCount === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-accent text-white hover:bg-blue-800'
              }`}
            >
              {selectedCount === 0
                ? 'Select at least one step'
                : `Execute (${selectedCount} step${selectedCount !== 1 ? 's' : ''})`}
            </button>
            <button
              onClick={onCancel}
              className="py-2 px-3 rounded text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
          </div>
        </>
      )}

      {locked && (
        <div className="flex items-center gap-2 px-4 py-2 text-xs text-gray-400">
          <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
          Executing…
        </div>
      )}
    </div>
  );
};

export default AgentPlanCard;
